/* A message WITH its pictures from the internet, when the reader asks (step 6).
 *
 * **The page's own policy never changes.** It cannot be loosened while the
 * page is open, and it must not be: it is what keeps every other message, and
 * every other mailbox, offline. So the message is drawn in an isolated frame
 * served from the module's own route with its own policy (pictures from
 * anywhere, nothing else), sandboxed with no origin so it can reach nothing of
 * ours. The page sanitises the message first, keeping remote picture
 * addresses and removing everything else; the frame only displays it.
 *
 * **Inline `cid:` pictures travel as `data:` URLs**, not the page's `blob:`
 * URLs: a frame with no origin may not be allowed to read the page's blobs.
 */

import { sanitize } from "./sanitize.js";
import { BODY_CSS, fillMeta } from "./view.js";

function dataUrl(buffer, type) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${type || "application/octet-stream"};base64,${btoa(binary)}`;
}

function inlinePictures(parsed) {
  const cids = new Map();
  for (const attachment of parsed.attachments || []) {
    if (!attachment.contentId) continue;
    cids.set(attachment.contentId.replace(/^<|>$/g, "").trim().toLowerCase(), dataUrl(attachment.content, attachment.mimeType));
  }
  return cids;
}

/** The frame's HTML: a print-only header, the body's own stylesheet, the message. */
function frameHtml(parsed) {
  const clean = sanitize(parsed.html || "", inlinePictures(parsed), { loadRemote: true });

  const head = document.createElement("header");
  head.className = "print-head";
  const h1 = document.createElement("h1");
  h1.textContent = parsed.subject || "(no subject)";
  const dl = document.createElement("dl");
  fillMeta(dl, parsed);
  for (const element of dl.children) element.removeAttribute("class");
  head.append(h1, dl);

  // BODY_CSS is written for a shadow root's :host; in a document it is the body.
  const css = BODY_CSS.replace(":host", ".mail-root");
  return `${head.outerHTML}<style>${css}</style>${clean.styles}<div class="mail-root"><div class="mail-html"><div class="mail-body">${clean.body}</div></div></div>`;
}

/**
 * Draws a message with its pictures into `host`. Returns a handle whose
 * `print(title)` prints the frame alone, header included.
 */
export function framedMessage(host, parsed, framePath) {
  const frame = document.createElement("iframe");
  // No allow-same-origin: the frame has no origin, so it can reach nothing of ours.
  frame.setAttribute("sandbox", "allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals");
  frame.setAttribute("referrerpolicy", "no-referrer");
  frame.title = "The message, with its pictures";
  // Inline styles, not classes: Tailwind cannot see a class set from a script.
  frame.style.cssText = "display:block;width:100%;border:0;height:12rem";
  frame.src = framePath;

  const html = frameHtml(parsed);
  const listener = (event) => {
    if (event.source !== frame.contentWindow || !event.data || event.data.mbFrame !== true) return;
    if (event.data.kind === "ready") frame.contentWindow.postMessage({ mbFrame: true, kind: "show", html }, "*");
    if (event.data.kind === "height") frame.style.height = `${Math.ceil(event.data.height)}px`;
  };

  window.addEventListener("message", listener);
  host.replaceChildren(frame);

  return {
    frame,
    print(title) {
      frame.contentWindow?.postMessage({ mbFrame: true, kind: "print", title }, "*");
    },
    dispose() {
      window.removeEventListener("message", listener);
    },
  };
}
